import { resolveApiUrl } from "./api-origin";
import type {
  ActiveCallEntry,
  ActiveDbCall,
  ProductDbClient,
} from "@constructorfleet/bhg-product-core";

const SESSION_TOKEN_KEY = "cf-session-token";

export type CallActivitySnapshot = {
  entries: ActiveCallEntry[];
  providerError: string | null;
};

export function subscribeCallActivity(
  onSnapshot: (snapshot: CallActivitySnapshot) => void,
  onError: () => void
): () => void {
  const token = encodeURIComponent(localStorage.getItem(SESSION_TOKEN_KEY) ?? "");
  const eventSource = new EventSource(
    resolveApiUrl(`/api/admin/bhg-call/call-activity-events?sessionToken=${token}`)
  );
  let closed = false;
  let errorNotified = false;

  const closeIfNeeded = () => {
    if (closed) {
      return;
    }
    closed = true;
    eventSource.close();
  };

  const notifyErrorOnce = () => {
    if (errorNotified) {
      return;
    }
    errorNotified = true;
    closeIfNeeded();
    onError();
  };

  eventSource.onmessage = (event) => {
    if (closed) {
      return;
    }
    try {
      const data = JSON.parse(event.data) as {
        entries?: unknown[];
        providerError?: string | null;
      };
      if (!Array.isArray(data.entries)) {
        notifyErrorOnce();
        return;
      }

      const entries: ActiveCallEntry[] = data.entries.map((item: unknown) => {
        const raw = item as Record<string, unknown>;
        const dbRaw = raw.dbCall as Record<string, unknown> | null | undefined;
        return {
          callSid: String(raw.callSid ?? ""),
          dbCall: dbRaw
            ? ({
                id: Number(dbRaw.id ?? 0),
                userId: Number(dbRaw.userId ?? 0),
                callSid: String(dbRaw.callSid ?? ""),
                status: String(dbRaw.status ?? ""),
                startedAt: String(dbRaw.startedAt ?? ""),
                voiceProvider: typeof dbRaw.voiceProvider === "string" ? dbRaw.voiceProvider : null,
                errorReason: typeof dbRaw.errorReason === "string" ? dbRaw.errorReason : null,
              } satisfies ActiveDbCall)
            : null,
          providerActive: Boolean(raw.providerActive),
        };
      });

      onSnapshot({
        entries,
        providerError: typeof data.providerError === "string" ? data.providerError : null,
      });
    } catch {
      notifyErrorOnce();
    }
  };
  eventSource.onerror = () => {
    notifyErrorOnce();
  };
  return () => {
    closeIfNeeded();
  };
}

export async function migrateLegacyBrowserStateToDb(dbClient: ProductDbClient) {
  if (!dbClient.requestAdmin || typeof localStorage === "undefined") {
    return;
  }
  const RUNTIME_SETTINGS_KEY = "constructorfleet:product:bhg-call:settings";
  const PHRASE_RULES_KEY = "constructorfleet:product:bhg-call:phrase-rules";
  const RESULT_RULES_KEY = "constructorfleet:product:bhg-call:result-rules";
  const migrationKey = "constructorfleet:product:bhg-call:migrated-to-db";
  if (localStorage.getItem(migrationKey) === "1") {
    return;
  }

  const runtimeRaw = localStorage.getItem(RUNTIME_SETTINGS_KEY);
  const phraseRaw = localStorage.getItem(PHRASE_RULES_KEY);
  const resultRaw = localStorage.getItem(RESULT_RULES_KEY);
  if (!runtimeRaw && !phraseRaw && !resultRaw) {
    localStorage.setItem(migrationKey, "1");
    return;
  }

  let migrated = true;
  const runtimeParsed = tryParseJson(runtimeRaw);
  const phraseParsed = tryParseJson(phraseRaw);
  const resultParsed = tryParseJson(resultRaw);

  if (runtimeParsed) {
    const response = await dbClient.requestAdmin({
      method: "POST",
      path: "/api/admin/bhg-call/runtime-settings",
      body: runtimeParsed,
    });
    migrated = migrated && response.ok;
  }
  if (phraseParsed) {
    const response = await dbClient.requestAdmin({
      method: "POST",
      path: "/api/admin/bhg-call/phrase-rules",
      body: phraseParsed,
    });
    migrated = migrated && response.ok;
  }
  if (resultParsed) {
    const response = await dbClient.requestAdmin({
      method: "POST",
      path: "/api/admin/bhg-call/result-rules",
      body: resultParsed,
    });
    migrated = migrated && response.ok;
  }

  if (migrated) {
    localStorage.removeItem(RUNTIME_SETTINGS_KEY);
    localStorage.removeItem(PHRASE_RULES_KEY);
    localStorage.removeItem(RESULT_RULES_KEY);
    localStorage.setItem(migrationKey, "1");
  }
}

function tryParseJson(raw: string | null) {
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
}
